/* Métricas de Negócio (espelha /metricas/negocio/visao-geral do ML).
   8 KPIs comparados com período anterior + gráfico tendência. */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, Filter, Download, TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react'
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'
import { api, formatMoney } from '@/lib/api'

type Kpi = { valor: number; valor_anterior: number; variacao_pct: number | null; fmt: 'R$' | 'num' | 'pct_pontos' }
type Resp = {
  periodo: { data_ini: string; data_fim: string; janela_dias: number }
  comparar: { data_ini: string; data_fim: string }
  kpis: {
    vendas_brutas: Kpi
    unidades_vendidas: Kpi
    preco_medio_unidade: Kpi
    visitas: Kpi
    qtd_vendas: Kpi
    conversao: Kpi
    preco_medio_venda: Kpi
    qtd_canceladas: Kpi
  }
  serie_vendas: { dia: string; vendas: number; qtd: number }[]
  serie_visitas: { dia: string; visitas: number }[]
  tem_visitas: boolean
}

const PERIODOS = [
  { key: '7d', label: 'Últimos 7 dias' },
  { key: '14d', label: 'Últimos 14 dias' },
  { key: '30d', label: 'Últimos 30 dias' },
  { key: '60d', label: 'Últimos 60 dias' },
  { key: '90d', label: 'Últimos 90 dias' },
]

function fmtValor(v: number, fmt: Kpi['fmt']) {
  if (fmt === 'R$') return formatMoney(v)
  if (fmt === 'pct_pontos') return `${v.toFixed(1)}%`
  return v.toLocaleString('pt-BR')
}

function Variacao({ pct, fmt }: { pct: number | null; fmt: Kpi['fmt'] }) {
  if (pct == null) return <span className="text-ink-400 text-xs">—</span>
  const positivo = pct > 0
  const cor = positivo ? 'text-emerald-600' : pct < 0 ? 'text-rose-600' : 'text-ink-500'
  const Icon = positivo ? TrendingUp : pct < 0 ? TrendingDown : Minus
  const label = fmt === 'pct_pontos'
    ? `${pct > 0 ? '+' : ''}${pct.toFixed(1)} pontos`
    : `${positivo ? '▲' : pct < 0 ? '▼' : ''} ${Math.abs(pct).toFixed(1)}%`
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${cor}`}>
      <Icon size={12} /> {label}
    </span>
  )
}

function KpiCard({ titulo, kpi }: { titulo: string; kpi: Kpi }) {
  return (
    <div className="bg-white rounded-md border border-ink-200 p-4 hover:shadow-sm transition">
      <div className="text-xs text-ink-600 mb-1">{titulo}</div>
      <div className="flex items-end justify-between gap-2">
        <div className="text-2xl font-bold text-ink-900">{fmtValor(kpi.valor, kpi.fmt)}</div>
        <Variacao pct={kpi.variacao_pct} fmt={kpi.fmt} />
      </div>
    </div>
  )
}

export default function MetricasNegocioMLPage() {
  const [periodo, setPeriodo] = useState('7d')

  const { data, isLoading } = useQuery<Resp>({
    queryKey: ['ml-metricas', periodo],
    queryFn: async () => (await api.get('/marketplace/ml-painel/metricas', { params: { periodo } })).data,
    refetchInterval: 5 * 60 * 1000, // 5min
  })

  if (isLoading || !data) {
    return <div className="p-10 text-center text-ink-400"><Loader2 className="animate-spin inline mr-2" size={16} /> Carregando…</div>
  }

  const k = data.kpis
  const labelPeriodo = PERIODOS.find(p => p.key === periodo)?.label || periodo

  return (
    <div className="p-4 lg:p-6 bg-slate-50 min-h-screen space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-ink-900">Métricas</h1>
        <div className="flex items-center gap-2">
          <button className="px-3 py-1.5 rounded text-sm font-semibold text-blue-600 hover:bg-blue-50">
            Gerar relatórios
          </button>
          <button className="px-3 py-1.5 rounded-full bg-rose-50 text-rose-700 text-sm font-semibold border border-rose-200 inline-flex items-center gap-1">
            <span className="inline-block w-2 h-2 bg-rose-500 rounded-full animate-pulse" />
            Painel de vendas ao vivo
          </button>
        </div>
      </div>

      {/* Tabs (Negócio / outros — ficamos só no Negócio por enquanto) */}
      <div className="flex gap-6 border-b border-ink-200">
        <div className="pb-2 border-b-2 border-blue-600 text-blue-600 font-semibold text-sm">Negócio</div>
        <div className="pb-2 text-sm text-ink-500">Promoções</div>
        <div className="pb-2 text-sm text-ink-500">Custos</div>
        <div className="pb-2 text-sm text-ink-500">Atendimento aos seus compradores</div>
        <div className="pb-2 text-sm text-ink-500">Desempenho em envios</div>
        <div className="pb-2 text-sm text-ink-500">Estoque Full</div>
        <div className="pb-2 text-sm text-ink-500">Análise de mercado</div>
        <div className="pb-2 text-sm text-ink-500">Minha página</div>
      </div>

      {/* Sub-tabs Visão geral / Anúncios */}
      <div className="flex gap-6 border-b border-ink-200">
        <div className="pb-2 border-b-2 border-blue-600 text-blue-600 font-semibold text-sm">Visão geral</div>
        <div className="pb-2 text-sm text-ink-500 flex items-center gap-1">
          Anúncios
          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
        </div>
      </div>

      {/* Filtros */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <label className="text-xs text-ink-600">
            Período principal
            <select
              value={periodo}
              onChange={e => setPeriodo(e.target.value)}
              className="block mt-1 input input-sm w-48"
            >
              {PERIODOS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </label>
          <label className="text-xs text-ink-600">
            Comparar com
            <select className="block mt-1 input input-sm w-48" disabled>
              <option>Período anterior</option>
            </select>
          </label>
          <button className="mt-5 inline-flex items-center gap-1 text-sm text-ink-700 hover:bg-ink-100 px-3 py-1.5 rounded">
            <Filter size={14} /> Filtrar
          </button>
        </div>
        <button className="text-sm text-blue-600 hover:underline">Preciso de ajuda</button>
      </div>

      {/* Resumo de desempenho */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-ink-700">Resumo de desempenho</h2>
          <button className="inline-flex items-center gap-1 text-sm text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded border border-blue-200">
            Baixar relatório <ChevronDown size={14} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 bg-white rounded-md border border-ink-200 p-4">
          <KpiCard titulo="Vendas brutas" kpi={k.vendas_brutas} />
          <KpiCard titulo="Unidades vendidas" kpi={k.unidades_vendidas} />
          <KpiCard titulo="Preço médio por unidade" kpi={k.preco_medio_unidade} />
          <KpiCard titulo="Visitas" kpi={k.visitas} />
          <KpiCard titulo="Quantidade de vendas" kpi={k.qtd_vendas} />
          <KpiCard titulo="Conversão" kpi={k.conversao} />
          <KpiCard titulo="Preço médio por venda" kpi={k.preco_medio_venda} />
          <KpiCard titulo="Quantidade de vendas canceladas" kpi={k.qtd_canceladas} />
        </div>

        {!data.tem_visitas && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            Nota: visitas e conversão dependem do consumo da API <code>/users/.../items_visits</code> do ML;
            valores podem aparecer zerados se a janela for grande ou conexão recém-criada.
          </div>
        )}
      </div>

      {/* Gráfico de tendência */}
      <div className="bg-white border border-ink-200 rounded-md p-4">
        <div className="text-sm font-semibold mb-3 text-ink-700">
          Tendência diária ({labelPeriodo.toLowerCase()})
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data.serie_vendas}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="dia" tick={{ fontSize: 10 }} />
            <YAxis yAxisId="r" tick={{ fontSize: 10 }} tickFormatter={v => `R$${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v: number) => formatMoney(v)} />
            <Line yAxisId="r" type="monotone" dataKey="vendas" stroke="#ec4899" strokeWidth={2} name="Vendas brutas" dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
